import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * PercentInput — input de porcentagem com sufixo "%" em tempo real.
 *
 * Comportamento:
 *   - Usuário digita dígitos normalmente. O componente renderiza "50%" no campo.
 *   - Suporta até 2 casas decimais (opcional via prop `decimals`).
 *   - Backspace remove o último dígito; apagar tudo retorna ao placeholder.
 *   - Clamp automático em min/max ao sair do campo.
 *
 * API:
 *   <PercentInput
 *     value={50}              // number (0-100 por default)
 *     onChange={v => setX(v)} // recebe number
 *     min={0}
 *     max={100}
 *     decimals={0}            // quantas casas decimais aceita (0, 1 ou 2)
 *     placeholder="0%"
 *   />
 *
 * Valor externo e onChange são sempre number (ex: 50 pra 50%, 12.5 pra 12.5%).
 */

function onlyDigits(str) {
  return String(str || '').replace(/\D/g, '');
}

// Formata unidades (inteiras ou decimais) em "X%" ou "X,X%"
function formatPercent(intValue, decimals) {
  if (decimals === 0) return `${intValue}%`;
  const divisor = Math.pow(10, decimals);
  const whole = Math.floor(intValue / divisor);
  const frac = String(intValue % divisor).padStart(decimals, '0');
  return `${whole},${frac}%`;
}

export default function PercentInput({
  value,
  onChange,
  min = 0,
  max = 100,
  decimals = 0,
  placeholder,
  disabled = false,
  autoFocus = false,
  name,
  id,
  required = false,
  className = '',
  style,
  ariaLabel,
}) {
  const multiplier = Math.pow(10, decimals);
  // State interno: número inteiro representando o valor × 10^decimals
  // Ex: decimals=2, value=12.5 → rawUnits=1250
  const [rawUnits, setRawUnits] = useState(() => {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (Number.isNaN(n) || n < 0) return '';
    return String(Math.round(n * multiplier));
  });

  // Sincroniza com value externo (pai). Usa ref pra evitar loop sem precisar
  // declarar rawUnits como dep (ele muda a cada onChange interno).
  const lastExternalRef = useRef(value);
  useEffect(() => {
    const ext = value === null || value === undefined || value === '' ? null : Number(value);
    const last = lastExternalRef.current === null || lastExternalRef.current === undefined || lastExternalRef.current === ''
      ? null : Number(lastExternalRef.current);
    if (ext === last) return;
    lastExternalRef.current = value;
    setRawUnits(ext === null || Number.isNaN(ext) ? '' : String(Math.round(ext * multiplier)));
  }, [value, multiplier]);

  const emit = useCallback((units) => {
    if (!onChange) return;
    const n = units === '' ? 0 : Number(units) / multiplier;
    onChange(n);
  }, [onChange, multiplier]);

  const handleChange = (e) => {
    if (disabled) return;
    const newValueRaw = e.target.value;
    const newDigits = onlyDigits(newValueRaw).slice(0, 6);

    // Deteção de backspace sobre o sufixo "%":
    // Quando o display é "2%" e usuário aperta backspace, o browser remove
    // só o "%" → newDigits vem IGUAL ao rawUnits atual, mas newValueRaw.length
    // < display.length. Isso travava a correção (nenhum dígito mudou) e
    // deixava o usuário preso no valor atual. Fix: se detectar esse caso,
    // corta o último dígito manualmente pra o backspace funcionar.
    const prevDisplay = rawUnits === '' ? '' : formatPercent(Number(rawUnits), decimals);
    if (newDigits === rawUnits && newValueRaw.length < prevDisplay.length) {
      const trimmed = rawUnits.slice(0, -1);
      setRawUnits(trimmed);
      emit(trimmed);
      return;
    }

    // Caso normal: user digitou ou removeu dígitos via edição normal.
    // Remove zeros à esquerda; se ficou vazio, mantém vazio (permite limpar
    // o campo totalmente pra mostrar placeholder).
    const trimmed = newDigits.replace(/^0+/, '');
    setRawUnits(trimmed);
    emit(trimmed);
  };

  const handleBlur = () => {
    if (rawUnits === '') return;
    let units = Number(rawUnits);
    const minUnits = Math.round(min * multiplier);
    const maxUnits = Math.round(max * multiplier);
    if (units < minUnits) units = minUnits;
    if (units > maxUnits) units = maxUnits;
    const newRaw = String(units);
    if (newRaw !== rawUnits) {
      setRawUnits(newRaw);
      emit(newRaw);
    }
  };

  const display = rawUnits === ''
    ? ''
    : formatPercent(Number(rawUnits), decimals);
  const effectivePlaceholder = placeholder || (decimals > 0 ? '0,00%' : '0%');

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      name={name}
      id={id}
      required={required}
      disabled={disabled}
      autoFocus={autoFocus}
      placeholder={effectivePlaceholder}
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      style={style}
      aria-label={ariaLabel}
    />
  );
}
